import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import CommonPopUp from './common-popup';
import { Methods } from '../../core/utility';
import { UploadService, LocalService } from '../../core/services';
import LinearProgress from '@mui/material/LinearProgress';
import * as gtUtil from "../../core/utility";
import { filterProps } from 'framer-motion';
import MaterialSymbol from '../icons/material-symbol';
import Cropper from 'react-easy-crop';
import { Slider, Tooltip, Typography } from '@mui/material';

const methods: Methods = new Methods();
const localService = new LocalService();

// zoom=1 is the cover-fit size (image exactly fills the crop frame with no
// gaps). Going below 1 would shrink the image smaller than the frame,
// exposing empty space at the edges instead of showing more of the photo —
// not useful for a fixed-aspect cover crop, so 1 stays the floor.
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

interface FileUploadPopupProps {
    openModal: boolean;
    onClose: (closed: boolean) => void;
    acceptedFileTypes: string;
    onSend: (data: any) => void;
    path: string;
    fileMinimumSize?: number; // in KB
    fileMaximumSize?: number;
    coverImage?: boolean,
    coverImageDimensionText?: string;
    /** Mount inside parent (e.g. Radix Dialog) so clicks work; default portals to body. */
    disablePortal?: boolean;
}

const FileUploadPopup: React.FC<FileUploadPopupProps> = ({
    openModal,
    onClose,
    onSend,
    acceptedFileTypes,
    path,
    fileMinimumSize = 0,
    coverImage,
    coverImageDimensionText,
    disablePortal = false,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [src, setSelectedImage] = useState<any>(null);
    const [error, setError] = useState({ status: false, message: '' });

    // Stable per-file blob URL — react-easy-crop reloads/redecodes the whole
    // image whenever its `image` prop reference changes, and it was
    // previously handed a brand-new URL.createObjectURL(file) on every
    // render (including every drag/zoom frame), which both stalled the crop
    // UI and leaked a blob URL each time since none were ever revoked.
    const fileObjectUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
    useEffect(() => {
        return () => {
            if (fileObjectUrl) URL.revokeObjectURL(fileObjectUrl);
        };
    }, [fileObjectUrl]);

    // Define size limits in KB
    const fileSizeLimits: Record<string, number> = {
        'video/mp4': 150 * 1024, // 150 MB
        'image/jpeg': 2 * 1024,  // 2 MB
        'image/png': 2 * 1024,   // 2 MB
        'image/jpg': 2 * 1024,   // 2 MB
        'application/pdf': 500,  // 500 KB
        'application/msword': 500, // 500 KB
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 500, // 500 KB
    };

    // uploadProgress tracks bytes leaving the browser, which reaches 100
    // as soon as the request body is sent — the server still has to process
    // the file and respond before the awaited call resolves. Without this,
    // the bar sits at a determinate 100% for that whole gap and looks stuck.
    const isProcessing = loading && uploadProgress >= 100;
    const uploadLabel = isProcessing ? 'Processing…' : `Uploading… ${uploadProgress}%`;

    const handleClosePreviewImage = () => {
        setFile(null);
    };

    // Upload file to server
    const uploadFile = () => {
        if (file != null && src != null) {
            setLoading(true);
            setUploadProgress(0);
            const form = new FormData();
            form.append('file', file, file.name);
            form.append('path', path);

            UploadService.UploadMedia(form, setUploadProgress)
                .then((res) => {
                    if (res.status === gtUtil.Constants.SUCCESS) {
                        localService.toastify(res.info, 'success', 1000);
                        onSend(res?.data?.rows);
                        onClose(false);
                    } else {
                        localService.toastify(res.info, 'error', 1000);
                    }
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                    localService.toastify('Error uploading file', 'error', 1000);
                });
        }
    };

    // Handle file selection
    const getImageHandle = (e: any) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            const fileType = selectedFile.type;
            const fileSize = selectedFile.size / 1024; // in KB
            const maxSize = fileSizeLimits[fileType] || 0;

            const isImage = fileType.startsWith('image/');

            if (fileSize < fileMinimumSize || fileSize > maxSize) {
                setError({
                    status: true,
                    message: `File size must not exceed ${maxSize}KB for this file type.`,
                });
                return;
            }

            if (isImage && coverImage) {
                setCrop({ x: 0, y: 0 });
                setZoom(1);
                setFile(selectedFile);
                setSelectedImage(URL.createObjectURL(selectedFile));
                setError({ status: false, message: '' });
            } else {
                setFile(selectedFile);
                setSelectedImage(URL.createObjectURL(selectedFile));
                setError({ status: false, message: '' });
            }
        }
    };
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)


    const [croppedBlob, setCroppedBlob] = useState(null);


    const getCroppedImg = async (imageSrc: any, croppedAreaPixels: any) => {
        const image = new Image();
        image.src = imageSrc;
        await new Promise((resolve) => { image.onload = resolve; });

        const canvas = document.createElement('canvas');
        const outputWidth = 1280;
        const outputHeight = 960;

        canvas.width = outputWidth;
        canvas.height = outputHeight;
        const ctx: any = canvas.getContext('2d');

        ctx.drawImage(
            image,
            croppedAreaPixels.x,
            croppedAreaPixels.y,
            croppedAreaPixels.width,
            croppedAreaPixels.height,
            0,
            0,
            outputWidth,
            outputHeight
        );
        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/jpeg', 0.85);
        });
    };
    const [croppedAreaPixels, setCroppedAreaPixels] = useState()
    const onCropComplete = useCallback(async (croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels); // store this in state
    }, []);
    const handleCropAndUpload = async () => {
        if (file && croppedAreaPixels && !loading) {
            setLoading(true);
            setUploadProgress(0);
            try {
                const croppedImageBlob: any = await getCroppedImg(fileObjectUrl as string, croppedAreaPixels);
                setCroppedBlob(croppedImageBlob);

                const croppedFile = new File([croppedImageBlob], file.name, { type: 'image/jpeg' });

                const form = new FormData();
                form.append('file', croppedFile, file.name);
                form.append('path', path);

                const res = await UploadService.UploadMedia(form, setUploadProgress);

                if (res.status === gtUtil.Constants.SUCCESS) {
                    const rows = res?.data?.rows;
                    localService.toastify("Cropped image uploaded successfully!", 'success', 1000);
                    onSend(rows);
                    onClose(false);
                } else {
                    localService.toastify(res.info, 'error', 1000);
                }
            } catch (error) {
                localService.toastify('Error uploading cropped image', 'error', 1000);
            } finally {
                setLoading(false);
            }
        }
    };


    return (
        <CommonPopUp
            modalTitle="Upload"
            modalContent=""
            openModal={openModal}
            onClose={() => onClose(false)}
            className="modal-medium mx-auto"
            disablePortal={disablePortal}
        >
            <div className="upload-doc-sec">
                <Fragment>
                    {/* {file && (
                        <div className="creative-image-preview-sec">
                            <span
                                className="material-symbols-rounded icon-filled close-preview"
                                onClick={handleClosePreviewImage}
                            >
                                cancel
                            </span>
                            {(file.type !== 'application/pdf' &&
                                file.type !== 'application/msword' &&
                                file.type !== 'video/mp4') && (
                                <>
                                 <Cropper
                                    image={URL.createObjectURL(file)}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={4 / 3}
                                    onCropChange={setCrop}
                                    onCropComplete={onCropComplete}
                                    onZoomChange={setZoom}
                                    
                                />
                                <img
                                    src={URL.createObjectURL(file)}
                                    alt=""
                                    className="creative-image-preview"
                                />
                                </>
                            )}
                            {file.type === 'video/mp4' && (
                                <video
                                    style={{ cursor: 'pointer' }}
                                    height={400}
                                    width={400}
                                    className="creative-image-preview"
                                    controls
                                >
                                    <source src={URL.createObjectURL(file)} type="video/mp4" />
                                </video>
                            )}
                            {(file.type === 'application/pdf') &&
                                <MaterialSymbol fontSize='48px' iconName='picture_as_pdf' fontweight='200' />
                                }
                                {(
                                file.type === 'application/msword' ||
                                file.type === 'mats-officedocument.wordprocessingml.document') &&
                                <MaterialSymbol fontSize='48px' iconName='description' fontweight='200' />
                                }
                        </div>
                    )} */}
                    {file && coverImage && (
                        <>
                            <div className="creative-image-preview-sec cover-image-crop-sec">
                                <span
                                    className="material-symbols-rounded icon-filled close-preview close-preview--inset"
                                    onClick={handleClosePreviewImage}
                                >
                                    cancel
                                </span>
                                {(file.type !== 'application/pdf' &&
                                    file.type !== 'application/msword' &&
                                    file.type !== 'video/mp4') && (
                                        <Cropper
                                            image={fileObjectUrl as string}
                                            crop={crop}
                                            zoom={zoom}
                                            minZoom={MIN_ZOOM}
                                            maxZoom={MAX_ZOOM}
                                            aspect={4 / 3}
                                            onCropChange={setCrop}
                                            onCropComplete={onCropComplete}
                                            onZoomChange={setZoom}
                                            objectFit="auto-cover"
                                            restrictPosition={true}
                                            cropShape="rect"
                                            showGrid={true}
                                        />
                                    )}
                            </div>
                            <div className="cover-image-zoom-slider">
                                <Typography variant="body2">
                                    Zoom : {Math.round(zoom * 100)}%
                                </Typography>
                                <Slider
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                                    min={MIN_ZOOM}
                                    max={MAX_ZOOM}
                                    step={0.1}
                                    value={zoom}
                                    onChange={(e, value) => setZoom(value as number)}
                                />
                            </div>
                        </>
                    )}



                    {file && !coverImage && (
                        <div className="creative-image-preview-sec">
                            <span
                                className="material-symbols-rounded icon-filled close-preview"
                                onClick={handleClosePreviewImage}
                            >
                                cancel
                            </span>
                            {(file.type !== 'application/pdf' &&
                                file.type !== 'application/msword' &&
                                file.type !== 'video/mp4') && (
                                    <img
                                        src={fileObjectUrl as string}
                                        alt=""
                                        className="creative-image-preview"
                                    />
                                )}
                            {file.type === 'video/mp4' && (
                                <video
                                    style={{ cursor: 'pointer' }}
                                    height={400}
                                    width={400}
                                    className="creative-image-preview"
                                    controls
                                >
                                    <source src={fileObjectUrl as string} type="video/mp4" />
                                </video>
                            )}
                            {(file.type === 'application/pdf') &&
                                <MaterialSymbol fontSize='48px' iconName='picture_as_pdf' fontweight='200' />
                            }
                            {(file.type === 'application/msword' ||
                                file.type === 'mats-officedocument.wordprocessingml.document') &&
                                <MaterialSymbol fontSize='48px' iconName='description' fontweight='200' />
                            }
                        </div>
                    )}
                    {!file && (
                        <div className="upload-btn">
                            <input type="file" accept={acceptedFileTypes} onChange={getImageHandle} />
                            <span className="material-symbols-rounded">upload</span>{' '}
                            <strong>Choose File</strong>
                        </div>
                    )}
                    {error.status && <span className="error">{error.message}</span>}

                    <span className="fs-14">accepts: {acceptedFileTypes}</span>
                    {coverImage && <span className='fs-14 d-block'>{coverImageDimensionText}</span>}
                    {!file && (
                        <>{(acceptedFileTypes === '.jpg, .jpeg, .png') &&
                            <span className='fs-14 pb-5 text-center'>
                                size limit : 2 MB</span>}

                            {(acceptedFileTypes === '.doc, .docx, .pdf') &&
                                <span className='fs-14 pb-5'>
                                    size limit : 500 KB.
                                </span>
                            }
                            {(acceptedFileTypes === '.mp4') &&
                                <span className='fs-14 pb-5'>size limit : 150 MB.</span>}
                        </>
                    )}
                    {file && (

                        <span className="fs-14 pb-5">
                            {/* {((file.type !== 'application/pdf')  &&  (file.type !== 
                        'application/msword') &&
        (file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) && <>
                            size limit : {((file.type !== 'application/pdf')  &&  (file.type !== 
                        'application/msword') &&
        (file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) && methods.decimalFormat((fileSizeLimits[file.type]) / 1024)} MB.</>} */}
                            {
                                // Check if file type is jpeg, png, or jpg
                                (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg') && (
                                    <>
                                        size limit: {fileSizeLimits[file.type] ? methods.decimalFormat(fileSizeLimits[file.type] / 1024) : 'N/A'} MB.
                                    </>
                                )
                            }
                            {file?.type !== 'image/jpeg' && file?.type !== 'image/png' && file?.type !== 'image/jpg' && (
                                <>
                                    size limit : {(file.type === 'application/pdf' || file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') && <>500 KB.</>
                                    }</>)
                            }

                        </span>
                    )}
                    {loading && (
                        <div className="uploading-progress">
                            <LinearProgress
                                variant={isProcessing ? 'indeterminate' : 'determinate'}
                                value={uploadProgress}
                            />
                            <span className="fs-14">
                                {isProcessing ? 'Processing, please don\'t close this window.' : `Uploading ${uploadProgress}%, please don't close this window.`}
                            </span>
                        </div>
                    )}
                    {coverImage && (
                        <>
                            {(file && !loading) &&
                                <p className='fs-14 pt-2 pb-2 mb-0'>Drag to reposition, use the slider (or mouse wheel / touchpad) to zoom</p>
                            }
                            <button disabled={!file || loading} className={`upload-file-btn btn btn-primary ${(!file || loading) ? 'disabled' : ''}`} onClick={handleCropAndUpload}>
                                {!file ? 'Upload' : loading ? uploadLabel : 'Apply Crop & Upload'}
                            </button>
                        </>
                    )}

                    {!coverImage && (
                        <button
                            className={`upload-file-btn btn btn-primary ${(!file || loading) ? 'disabled' : ''}`}
                            disabled={!file || loading}
                            onClick={uploadFile}
                        >
                            {loading ? uploadLabel : 'Upload'}
                        </button>
                    )}
                </Fragment>
            </div>
        </CommonPopUp>
    );
};

export default FileUploadPopup;